import { cloneDeep, isEmpty } from "lodash";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import { applicationAlreadyExists } from "@/components/ExperiencePage/utils.js";
import useExperiences from "@/components/MyExperiences/composables/useExperiences.js";
import {
  hasConfigurableRegistrationEnabled,
  hasExperienceDetailsPageEnabled,
  isStudent,
} from "@/composables/authorization.js";
import { ERROR_TIMEOUT, ORDER_STATUS, REGISTER_TYPE } from "@/constants.js";
import {
  checkForLegacyMapping,
  checkForProgramLengthLegacyMapping,
  convertTimeFrameToTerms,
} from "./legacyUrlIdMappings";
import { filterOutTenantFilters } from "./utils";

const LEGACY_PARAM_SEPARATOR = "#$#";
const PARAM_SEPARATOR = ":=";
const LEGACY_PRICE_SEPARATOR = "|";

const toast = useToast();

export const displayNewCardDesign = computed(() => {
  const store = useStore();
  return store.state.featureFlags?.["program-session-card-redesign"];
});

export const decodedParam = (decodedString) => {
  const parts = decodedString.split(LEGACY_PARAM_SEPARATOR);
  return parts;
};

export const getDecodedData = (decodedString, what = "value") => {
  const parts = decodedParam(decodedString);
  const position = what === "label" ? 0 : 1;
  return parts.length > 1 ? parts[position] : decodedString;
};

/**
 *
 * @param {*} initialKeys -> an array of keys where we will store each query param
 * it has to match the query key example:
 * if the query string look like this -> results?startDate=2023-06-05&endDate=2023-06-07
 * we need an array with the following:
 * initialKeys=[`startDate`,`endDate`]
 */
export const useQueryParamsToState = (route, initialKeys) => {
  if (!isEmpty(initialKeys)) {
    let decodedValue = []; //container for the decoded url param in case it is encoded
    const result = cloneDeep(initialKeys);

    Object.keys(route.query).forEach((urlKey) => {
      const queryValues = Array.isArray(route.query[urlKey]) ? route.query[urlKey] : [route.query[urlKey]];

      queryValues.forEach((queryValue) => {
        const decodedQueryValue = decodeURIComponent(queryValue);
        if (urlKey === "keyword" || urlKey === "classTitle") {
          result[urlKey] = decodedQueryValue;
        } else if (urlKey === "startDate" || urlKey === "endDate") {
          if (decodedQueryValue) {
            const obj = {
              label: urlKey === "startDate" ? "Start Date: " + decodedQueryValue : "End Date: " + decodedQueryValue,
              value: decodedQueryValue,
            };
            result[urlKey].push(obj);
          }
        } else if (urlKey === "pastPrograms") {
          const pastPrograms = decodedQueryValue;
          result["pastPrograms"] = pastPrograms === "true";
        } else if (urlKey === "cities") {
          if (decodedQueryValue.includes(LEGACY_PARAM_SEPARATOR)) {
            const paramParts = decodedParam(decodedQueryValue);
            if (paramParts.length === 2) {
              const label = getDecodedData(decodedQueryValue, "label");
              result[urlKey].push(label.split(",")[0]);
            }
          } else {
            const label = decodedQueryValue.split(",")[0];
            if (label) {
              const convertedLegacyMapping = checkForLegacyMapping("cities", label);
              result[urlKey].push(convertedLegacyMapping);
            }
          }
        } else if (urlKey === "price") {
          decodedValue = getDecodedData(decodedQueryValue).split(LEGACY_PRICE_SEPARATOR);
          const filteredVal = decodedValue.map((element) => +element).filter((val) => Number(val) >= 0);
          if (result["price"]?.length === 0) {
            if (decodedQueryValue.includes(LEGACY_PRICE_SEPARATOR)) {
              // If legacy price times 100
              result["price"] = filteredVal.map((val) => {
                const converted = Number(val);
                if (val) {
                  return converted * 100;
                }
                return 0;
              });
            } else {
              result["price"] = filteredVal;
            }
          } else if (result["price"]?.length < 3) {
            result["price"] = [...result["price"], ...filteredVal];
          }
        } else if (urlKey === "session_id" || urlKey === "sessionId") {
          let parts = [];
          if (decodedQueryValue.includes(LEGACY_PARAM_SEPARATOR)) {
            parts = decodedParam(decodedQueryValue);
          } else {
            parts = decodedQueryValue?.split(PARAM_SEPARATOR);
          }
          result["sessionId"] = parts?.length === 2 ? parts : [];
        } else if (urlKey === "programLength") {
          const convertedLegacyMapping = convertTimeFrameToTerms(
            checkForProgramLengthLegacyMapping(decodedQueryValue),
          );
          if (convertedLegacyMapping?.length > 0) {
            const termsSet = new Set(result["terms"]).union(convertedLegacyMapping);
            result["terms"] = [...termsSet];
          }
        } else if (urlKey === "timeframe") {
          const convertedLegacyMapping = convertTimeFrameToTerms(decodedQueryValue);
          if (convertedLegacyMapping?.length > 0) {
            const termsSet = new Set(result["terms"]).union(convertedLegacyMapping);
            result["terms"] = [...termsSet];
          }
        } else {
          if (result[urlKey] && decodedQueryValue) {
            if (decodedQueryValue.includes(LEGACY_PARAM_SEPARATOR)) {
              const paramParts = decodedParam(decodedQueryValue);
              if (paramParts.length === 2) {
                result[urlKey].push(getDecodedData(decodedQueryValue, "label"));
              }
            } else {
              const convertedLegacyMapping = checkForLegacyMapping(urlKey, decodedQueryValue);
              result[urlKey].push(convertedLegacyMapping);
            }
          }
        }
      });
    });
    return result;
  }
};

export const useConvertToUrlParams = (filters) => {
  const newFilters = {
    ...filters,
    sessionId: filters?.["sessionId"]?.length === 2 ? filters?.["sessionId"].join(PARAM_SEPARATOR) : "",
  };

  const filteredFilters = filterOutTenantFilters(newFilters);

  // Create non-empty filters
  const nonEmptyFilters = Object.keys(filteredFilters).reduce((acc, key) => {
    if (
      filteredFilters[key] &&
      ((Array.isArray(filteredFilters[key]) && filteredFilters[key]?.length > 0) || filteredFilters[key])
    ) {
      acc[key] = filteredFilters[key];
    }
    return acc;
  }, {});

  return nonEmptyFilters;
};

const isSelectingText = ref(false);
export const onMouseUp = (event) => {
  // Check if the user is selecting text
  isSelectingText.value = event.target.ownerDocument.getSelection().type === "Range";
};
export const handleClick = (emit) => {
  if (isSelectingText.value) {
    // If text is being selected, don't proceed with click logic
    return;
  }
  emit("viewClicked");
};
/**
 * Gets the /student_summary endpoint to determine what sessions the user has
 * open orders for.
 * showApplicationTakenModal: boolean to open ApplicationTakenModal
 * onApplyClicked: routes user to correct page based on existing order status.
 * When an internshipId is supplied, it is passed along so the configurator
 * pre-selects that internship.
 * getRegisterType: generates apply button copy based on existing order status
 * @returns {{
 * showApplicationTakenModal: Ref<UnwrapRef<boolean>, UnwrapRef<boolean> | boolean>,
 * onApplyClicked: ((function({program_page: *, session: *, internshipId: *}=): Promise<void>)|*),
 * getRegisterType: (function(*): string)
 * }}
 */
export const useApplicationProgress = () => {
  const router = useRouter();
  const store = useStore();
  const isLoggedIn = computed(() => store.getters.isLoggedIn);
  const { experiences, experiencesIsLoading } = useExperiences();
  const showApplicationTakenModal = ref(false);

  const showApplicationExistsModal = async ({ session } = {}) => {
    const { studentApplications } = store.state;
    const exists = await applicationAlreadyExists({
      programSession: session,
      studentApplications,
      skipRequest: false,
    });
    showApplicationTakenModal.value = exists;
    return exists;
  };

  const routeToExistingApplication = async ({ session } = {}) => {
    const existingApp = experiences?.value?.find((experience) => experience.session_id === session.id);
    if (existingApp && existingApp?.order_status === ORDER_STATUS.draft) {
      await router.push({
        name: "modify-application",
        params: { applicationId: existingApp?.application_id },
      });
    } else if (existingApp && hasExperienceDetailsPageEnabled.value) {
      await router.push({
        name: "experience-detail",
        params: { applicationId: existingApp?.application_id },
      });
    } else if (existingApp) {
      await router.push({
        name: "applications/landing",
        params: { applicationId: existingApp?.application_id },
      });
    }
    return !!existingApp;
  };

  const onApplyClicked = async ({ program_page: programPage, session, internshipId } = {}) => {
    let exists;
    let route;
    if (hasConfigurableRegistrationEnabled.value) {
      if (isLoggedIn.value && !isStudent() && !session?.is_personalized) {
        toast.open({
          message: "Registration creation is not allowed for your account type.",
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
        return;
      }
      exists = await routeToExistingApplication({ session });
      route = {
        name: "new-application",
        params: { programPageId: programPage?.id, sessionId: session?.id },
      };
      if (internshipId && session?.is_personalized) {
        route.params.stage = "personalize";
        route.query = { internships: [internshipId] };
      }
    } else {
      exists = await showApplicationExistsModal({ session });
      route = {
        name: "configurator-new",
        params: { programPageId: programPage?.id },
        query: { session: session?.id },
      };
    }
    if (!exists) {
      await router.push(route);
    }
  };

  /**
   * This method is not reactive so the session type must be populated when passed as an argument.
   * Currently, it is only being used with a list of sessions in a v-for loop.
   * @param session
   * @returns {string}
   */
  const getRegisterType = (session) => {
    if (!hasConfigurableRegistrationEnabled.value) return REGISTER_TYPE.PERSONALIZE_APPLY;
    let registerType = REGISTER_TYPE.GET_STARTED;
    if (!store.state.currentUser) return registerType;
    const existingExperience = experiences.value?.find((experience) => experience.session_id === session.id);
    if (existingExperience) {
      if (existingExperience.order_status === ORDER_STATUS.draft) {
        registerType = session.requires_application
          ? REGISTER_TYPE.CONTINUE_APPLICATION
          : REGISTER_TYPE.CONTINUE_REGISTRATION;
      } else if (
        [ORDER_STATUS.submitted, ORDER_STATUS.withdrawn, ORDER_STATUS.fulfilled, ORDER_STATUS.paidConfirmed].includes(
          existingExperience.order_status,
        )
      ) {
        registerType = REGISTER_TYPE.CONTINUE_ENROLLMENT;
      }
    }
    return registerType;
  };

  const isReady = computed(() => {
    return !experiencesIsLoading.value;
  });

  return {
    isReady,
    onApplyClicked,
    showApplicationTakenModal,
    getRegisterType,
  };
};
